import React, { useEffect, useMemo, useState } from "react";
import { View, Text, TextInput, Pressable, FlatList, ActivityIndicator, Alert, StyleSheet } from "react-native";
import { auth, db } from "@/lib/firebase";
import NamePromptModal from "@/components/admin/NamePromptModal";
import {
  type ModerationStatus,
  adminListCounties,
  adminListCities,
  adminListAreas,
  adminCreateApprovedArea,
  adminAreaNameExists,
  adminRenameArea,
  adminApproveArea,
  adminRejectArea,
  adminSoftDeleteArea,
  adminRestoreArea,
} from "@/lib/locationsAdmin";

type County = any;
type City = any;
type Area = any;

const STATUS_TABS: ModerationStatus[] = ["approved", "pending", "rejected"];

export default function AdminAreasManager() {
  const adminUid = auth.currentUser?.uid;

  const [counties, setCounties] = useState<County[]>([]);
  const [countyLoading, setCountyLoading] = useState(true);
  const [selectedCountyId, setSelectedCountyId] = useState<string | null>(null);

  const [cities, setCities] = useState<City[]>([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [selectedCityId, setSelectedCityId] = useState<string | null>(null);

  const [status, setStatus] = useState<ModerationStatus>("approved");
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Area[]>([]);
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editing, setEditing] = useState<Area | null>(null);

  const selectedCity = useMemo(
    () => cities.find((c) => c.id === selectedCityId) ?? null,
    [cities, selectedCityId]
  );

  function requireCountyId(): string {
    const id = selectedCountyId ?? "";
    if (!id) throw new Error("Pick a county first.");
    return id;
  }

  function requireCityId(): string {
    const id = selectedCityId ?? "";
    if (!id) throw new Error("Pick a city first.");
    return id;
  }

  async function loadCounties() {
    if (!adminUid) return;
    setCountyLoading(true);
    try {
      const data = await adminListCounties(db);
      setCounties(data);
      if (!selectedCountyId && data.length) setSelectedCountyId(data[0].id);
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Failed to load counties.");
    } finally {
      setCountyLoading(false);
    }
  }

  async function loadCities() {
    if (!adminUid) return;
    if (!selectedCountyId) {
      setCities([]);
      setSelectedCityId(null);
      return;
    }

    setCityLoading(true);
    try {
      // For selector: show approved + non-deleted cities only
      const data = await adminListCities(db, {
        countyId: selectedCountyId,
        status: "approved",
        includeDeleted: false,
      });
      setCities(data);
      if (!data.find((c) => c.id === selectedCityId)) {
        setSelectedCityId(data.length ? data[0].id : null);
      }
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Failed to load cities.");
    } finally {
      setCityLoading(false);
    }
  }

  async function loadAreas() {
    if (!adminUid) return;
    if (!selectedCountyId || !selectedCityId) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await adminListAreas(db, {
        countyId: selectedCountyId,
        cityId: selectedCityId,
        status,
        includeDeleted,
      });
      setRows(data);
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Failed to load areas.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCounties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUid]);

  useEffect(() => {
    loadCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUid, selectedCountyId]);

  useEffect(() => {
    loadAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUid, selectedCountyId, selectedCityId, status, includeDeleted]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r?.name ?? "").toLowerCase().includes(q));
  }, [rows, search]);

  function openAdd() {
    if (!selectedCountyId || !selectedCityId) {
      Alert.alert("Select location", "Pick county and city first.");
      return;
    }
    setModalMode("add");
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(area: Area) {
    setModalMode("edit");
    setEditing(area);
    setModalOpen(true);
  }

  async function handleSaveName(name: string) {
    if (!adminUid) return;

    try {
      const countyId = requireCountyId();
      const cityId = requireCityId();

      if (modalMode === "add") {
        const exists = await adminAreaNameExists(db, countyId, cityId, name);
        if (exists) {
          Alert.alert("Already exists", `"${name}" already exists in this city.`);
          return;
        }
        await adminCreateApprovedArea(db, { countyId, cityId, name, adminUid });
      } else if (editing?.id) {
        await adminRenameArea(db, { countyId, cityId, areaId: editing.id, name, adminUid });
      }

      setModalOpen(false);
      await loadAreas();
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Failed to save.");
    }
  }

  async function approve(area: Area) {
    if (!adminUid) return;
    try {
      const countyId = requireCountyId();
      const cityId = requireCityId();
      await adminApproveArea(db, { countyId, cityId, areaId: area.id, adminUid });
      await loadAreas();
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Approve failed.");
    }
  }

  async function reject(area: Area) {
    if (!adminUid) return;
    Alert.alert("Reject area?", `Reject "${area.name}"?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Reject",
        style: "destructive",
        onPress: async () => {
          try {
            const countyId = requireCountyId();
            const cityId = requireCityId();
            await adminRejectArea(db, { countyId, cityId, areaId: area.id, adminUid });
            await loadAreas();
          } catch (e: any) {
            Alert.alert("Error", e?.message ?? "Reject failed.");
          }
        },
      },
    ]);
  }

  async function toggleDelete(area: Area) {
    if (!adminUid) return;

    try {
      const countyId = requireCountyId();
      const cityId = requireCityId();

      if (area.isDeleted) {
        await adminRestoreArea(db, { countyId, cityId, areaId: area.id, adminUid });
        await loadAreas();
        return;
      }

      Alert.alert("Delete area?", `Soft delete "${area.name}"?`, [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            try {
              await adminSoftDeleteArea(db, { countyId, cityId, areaId: area.id, adminUid });
              await loadAreas();
            } catch (e: any) {
              Alert.alert("Error", e?.message ?? "Delete failed.");
            }
          },
        },
      ]);
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Action failed.");
    }
  }

  return (
    <View style={{ flex: 1 }}>
      {/* County selector */}
      {countyLoading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator />
          <Text style={{ opacity: 0.7 }}>Loading counties…</Text>
        </View>
      ) : (
        <View style={styles.selectorRow}>
          <Text style={styles.selectorLabel}>County:</Text>
          <View style={styles.selectorWrap}>
            <FlatList
              horizontal
              showsHorizontalScrollIndicator={false}
              data={counties}
              keyExtractor={(i) => i.id}
              contentContainerStyle={{ gap: 8 }}
              renderItem={({ item }) => {
                const active = item.id === selectedCountyId;
                return (
                  <Pressable
                    onPress={() => setSelectedCountyId(item.id)}
                    style={[styles.catPill, active && styles.catPillActive]}
                  >
                    <Text style={[styles.catPillText, active && styles.catPillTextActive]}>
                      {item.name}
                    </Text>
                  </Pressable>
                );
              }}
            />
          </View>

          <Pressable onPress={loadCounties} style={styles.ghostBtn}>
            <Text style={styles.ghostBtnText}>Refresh</Text>
          </Pressable>
        </View>
      )}

      {/* City selector */}
      {cityLoading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator />
          <Text style={{ opacity: 0.7 }}>Loading cities…</Text>
        </View>
      ) : (
        <View style={styles.selectorRow}>
          <Text style={styles.selectorLabel}>City:</Text>
          <View style={styles.selectorWrap}>
            <FlatList
              horizontal
              showsHorizontalScrollIndicator={false}
              data={cities}
              keyExtractor={(i) => i.id}
              contentContainerStyle={{ gap: 8 }}
              renderItem={({ item }) => {
                const active = item.id === selectedCityId;
                return (
                  <Pressable
                    onPress={() => setSelectedCityId(item.id)}
                    style={[styles.catPill, active && styles.catPillActive]}
                  >
                    <Text style={[styles.catPillText, active && styles.catPillTextActive]}>
                      {item.name}
                    </Text>
                  </Pressable>
                );
              }}
            />
          </View>

          <Pressable onPress={loadCities} style={styles.ghostBtn}>
            <Text style={styles.ghostBtnText}>Refresh</Text>
          </Pressable>
        </View>
      )}

      {/* Status tabs */}
      <View style={styles.row}>
        {STATUS_TABS.map((s) => (
          <Pressable key={s} onPress={() => setStatus(s)} style={[styles.pill, status === s && styles.pillActive]}>
            <Text style={[styles.pillText, status === s && styles.pillTextActive]}>{s.toUpperCase()}</Text>
          </Pressable>
        ))}
      </View>

      {/* Search + add */}
      <View style={styles.row}>
        <TextInput value={search} onChangeText={setSearch} placeholder="Search areas…" style={[styles.input, { flex: 1 }]} />
        <Pressable onPress={openAdd} style={styles.primaryBtn}>
          <Text style={styles.primaryBtnText}>+ Add</Text>
        </Pressable>
      </View>

      {/* toggles */}
      <View style={styles.row}>
        <Pressable onPress={() => setIncludeDeleted((v) => !v)} style={[styles.toggle, includeDeleted && styles.toggleOn]}>
          <Text style={[styles.toggleText, includeDeleted && styles.toggleTextOn]}>
            Include Deleted: {includeDeleted ? "ON" : "OFF"}
          </Text>
        </Pressable>

        <Pressable onPress={loadAreas} style={styles.ghostBtn}>
          <Text style={styles.ghostBtnText}>Refresh</Text>
        </Pressable>
      </View>

      {loading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator />
          <Text style={{ opacity: 0.7 }}>Loading…</Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={(item) => item.id}
          ItemSeparatorComponent={() => <View style={{ height: 10 }} />}
          renderItem={({ item }) => (
            <View style={[styles.card, item.isDeleted && { opacity: 0.6 }]}>
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>
                  {item.name} {item.isDeleted ? <Text style={styles.badge}>DELETED</Text> : null}
                </Text>
                <Text style={styles.meta}>status: {item.status} · id: {item.id}</Text>
              </View>

              <View style={styles.actions}>
                <Pressable onPress={() => openEdit(item)} style={styles.btn}>
                  <Text style={styles.btnText}>Edit</Text>
                </Pressable>

                {item.status === "pending" ? (
                  <>
                    <Pressable onPress={() => approve(item)} style={[styles.btn, styles.btnGood]}>
                      <Text style={[styles.btnText, styles.btnTextGood]}>Approve</Text>
                    </Pressable>
                    <Pressable onPress={() => reject(item)} style={[styles.btn, styles.btnBad]}>
                      <Text style={[styles.btnText, styles.btnTextBad]}>Reject</Text>
                    </Pressable>
                  </>
                ) : null}

                <Pressable onPress={() => toggleDelete(item)} style={[styles.btn, styles.btnBad]}>
                  <Text style={[styles.btnText, styles.btnTextBad]}>{item.isDeleted ? "Restore" : "Delete"}</Text>
                </Pressable>
              </View>
            </View>
          )}
          ListEmptyComponent={
            <View style={{ padding: 16 }}>
              <Text style={{ opacity: 0.7 }}>
                {selectedCity ? `No areas for "${selectedCity.name}".` : "Pick a county and city."}
              </Text>
            </View>
          }
        />
      )}

      <NamePromptModal
        visible={modalOpen}
        title={modalMode === "add" ? "Add Area (Approved)" : "Edit Area"}
        description={
          modalMode === "add"
            ? `City: ${selectedCity?.name ?? "-"} (created as APPROVED)`
            : "Rename the area (keeps same ID)."
        }
        initialValue={modalMode === "edit" ? editing?.name : ""}
        confirmText={modalMode === "add" ? "Create" : "Save"}
        onCancel={() => setModalOpen(false)}
        onConfirm={handleSaveName}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  selectorRow: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 10 },
  selectorLabel: { fontWeight: "900" },
  selectorWrap: { flex: 1 },

  catPill: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 999,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: "#fafafa",
  },
  catPillActive: { backgroundColor: "#111", borderColor: "#111" },
  catPillText: { fontWeight: "900", fontSize: 12 },
  catPillTextActive: { color: "white" },

  row: { flexDirection: "row", gap: 10, alignItems: "center", marginBottom: 10 },
  pill: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 999,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: "#fafafa",
  },
  pillActive: { backgroundColor: "#111", borderColor: "#111" },
  pillText: { fontWeight: "900", fontSize: 12 },
  pillTextActive: { color: "white" },

  input: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },

  primaryBtn: {
    backgroundColor: "#111",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  primaryBtnText: { color: "white", fontWeight: "900" },

  ghostBtn: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  ghostBtnText: { fontWeight: "900" },

  toggle: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: "#fafafa",
  },
  toggleOn: { backgroundColor: "#111", borderColor: "#111" },
  toggleText: { fontWeight: "900" },
  toggleTextOn: { color: "white" },

  loadingRow: { flexDirection: "row", gap: 8, alignItems: "center", padding: 12 },

  card: {
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 14,
    padding: 12,
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-start",
    backgroundColor: "white",
  },
  cardTitle: { fontSize: 15, fontWeight: "900" },
  badge: { fontSize: 12, fontWeight: "900", opacity: 0.6 },
  meta: { marginTop: 4, opacity: 0.7, fontSize: 12 },

  actions: { gap: 8, alignItems: "flex-end" },
  btn: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  btnText: { fontWeight: "900" },
  btnGood: { borderColor: "#2e7d32" },
  btnTextGood: { color: "#2e7d32" },
  btnBad: { borderColor: "#c62828" },
  btnTextBad: { color: "#c62828" },
});